/**
 * Account › Profile — view and edit the user's name, username, and email.
 * Extracted from Account.tsx (which had outgrown its complexity budget) so
 * the editing state lives with the fields it drives (P8).
 *
 * - Name (given/family) + Username (preferred_username): a single
 *   updateUserAttributes call, no verification.
 * - Email: updateUserAttributes marks it unverified and sends a code;
 *   a confirm step verifies it. Email is the magic-link login identity, so
 *   the UI warns before changing it.
 */
import { useState } from 'react';
import {
  confirmUserAttribute,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { humanizeAuthError } from '../../auth/errors.ts';
import UsernameInput from '../../components/auth/UsernameInput.tsx';
import type { AuthUser } from '../../auth/AuthContext.ts';

interface Props {
  readonly user: AuthUser | null;
  /** Re-syncs AuthContext after a saved change so the page reflects it. */
  readonly refresh: () => Promise<boolean>;
  /** Hidden while the SMS wizard's code step owns the page. */
  readonly hidden?: boolean;
}

type Mode = 'view' | 'edit-profile' | 'edit-email' | 'confirm-email';

export default function AccountProfileSection({ user, refresh, hidden = false }: Props) {
  const [mode, setMode] = useState<Mode>('view');
  const [givenName, setGivenName] = useState(user?.givenName ?? '');
  const [familyName, setFamilyName] = useState(user?.familyName ?? '');
  const [username, setUsername] = useState(user?.preferredUsername ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [code, setCode] = useState('');
  const [usernameValid, setUsernameValid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const resetFromUser = () => {
    setGivenName(user?.givenName ?? '');
    setFamilyName(user?.familyName ?? '');
    setUsername(user?.preferredUsername ?? '');
    setEmail(user?.email ?? '');
    setCode('');
    setError(null);
    setNotice(null);
  };

  const saveProfile = () => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await updateUserAttributes({
          userAttributes: {
            given_name: givenName.trim(),
            family_name: familyName.trim(),
            preferred_username: username.trim(),
          },
        });
        await refresh();
        setMode('view');
        setNotice('Profile updated.');
      } catch (err) {
        setError(humanizeAuthError(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  const startEmailChange = () => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const out = await updateUserAttributes({ userAttributes: { email: email.trim() } });
        const step = out.email.nextStep.updateAttributeStep;
        if (step === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
          setMode('confirm-email');
          setNotice(`We sent a verification code to ${email.trim()}.`);
        } else {
          await refresh();
          setMode('view');
          setNotice('Email updated.');
        }
      } catch (err) {
        setError(humanizeAuthError(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  const confirmEmail = () => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code.trim() });
        await refresh();
        setMode('view');
        setNotice('Email verified and updated.');
      } catch (err) {
        setError(humanizeAuthError(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section className="om-account__section" hidden={hidden}>
      <h2 className="om-account__h2">Profile</h2>

      {error !== null ? (
        <p className="om-account__error" role="alert">
          {error}
        </p>
      ) : null}
      {notice !== null && mode === 'view' ? (
        <p className="om-account__notice">{notice}</p>
      ) : null}

      {mode === 'view' ? (
        <>
          <dl className="om-account__dl">
            <div>
              <dt>Username</dt>
              <dd>{user?.preferredUsername ? `@${user.preferredUsername}` : '—'}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user?.email ?? '—'}</dd>
            </div>
            <div>
              <dt>Name</dt>
              <dd>
                {user?.givenName} {user?.familyName}
              </dd>
            </div>
            <div>
              <dt>User ID</dt>
              <dd className="om-account__mono">{user?.sub ?? '—'}</dd>
            </div>
          </dl>
          <div className="om-account__sms-actions om-account__sms-actions--spaced">
            <button
              type="button"
              className="om-account__action"
              onClick={() => {
                resetFromUser();
                setMode('edit-profile');
              }}
            >
              Edit name &amp; username
            </button>
            <button
              type="button"
              className="om-account__action"
              onClick={() => {
                resetFromUser();
                setMode('edit-email');
              }}
            >
              Change email
            </button>
          </div>
        </>
      ) : null}

      {mode === 'edit-profile' ? (
        <div className="om-account__edit">
          <label className="om-account__label">
            First name
            <input
              className="om-account__input"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              autoComplete="given-name"
            />
          </label>
          <label className="om-account__label">
            Last name
            <input
              className="om-account__input"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              autoComplete="family-name"
            />
          </label>
          <UsernameInput
            label="Username"
            value={username}
            onChange={setUsername}
            disabled={busy}
            onValidityChange={setUsernameValid}
            ownValue={user?.preferredUsername}
          />
          <div className="om-account__sms-actions">
            <button
              type="button"
              className="om-account__action"
              disabled={busy || !usernameValid}
              onClick={saveProfile}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="om-account__action"
              disabled={busy}
              onClick={() => {
                resetFromUser();
                setMode('view');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'edit-email' ? (
        <div className="om-account__edit">
          <p className="om-account__notice">
            Your email is how you sign in. You&rsquo;ll verify the new address with a code before
            it takes effect.
          </p>
          <label className="om-account__label">
            New email
            <input
              className="om-account__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
          <div className="om-account__sms-actions">
            <button
              type="button"
              className="om-account__action"
              disabled={busy || email.trim() === (user?.email ?? '')}
              onClick={startEmailChange}
            >
              {busy ? 'Sending…' : 'Send verification code'}
            </button>
            <button
              type="button"
              className="om-account__action"
              disabled={busy}
              onClick={() => {
                resetFromUser();
                setMode('view');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {mode === 'confirm-email' ? (
        <div className="om-account__edit">
          {notice !== null ? <p className="om-account__notice">{notice}</p> : null}
          <label className="om-account__label">
            Verification code
            <input
              className="om-account__input"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
            />
          </label>
          <div className="om-account__sms-actions">
            <button
              type="button"
              className="om-account__action"
              disabled={busy || code.trim() === ''}
              onClick={confirmEmail}
            >
              {busy ? 'Verifying…' : 'Verify email'}
            </button>
            <button
              type="button"
              className="om-account__action"
              disabled={busy}
              onClick={() => {
                resetFromUser();
                setMode('view');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
